import React from 'react';
import * as R from 'ramda';

import Page from 'ha/modules/Page';
import { SEARCH } from 'ha/constants/pageNames';
import SomethingWentWrongPage from 'ha/pages/SomethingWentWrongPage';
import NotFoundPage from 'ha/pages/NotFound';
import SearchLayout from './SearchLayout';
import { RequiredAnalyticsFilters } from '../constants';

interface Props {
  error: boolean;
  notFound: boolean;
  cityCanonical: string;
  countryCode: string;
  pageName: string;
  searchParams: {
    kinds: string[];
  };
  location: { query: { page: string }; pathname: string };
  filtersAnalytics: {
    numberOfPartnerListings: number;
    numberOfOrganicListings: number;
    latitude: number;
    longitude: number;
    zoomLevel: number;
    listingIdResults: number[];
    endDate: string;
    startDate: string;
    priceMax: number | string;
    priceMin: number | string;
    searchChanges?: string[];
  };
  listingsAnalytics: {
    numberOfSearchResults: number;
    numberOfPartnerListings: number;
    numberOfOrganicListings: number;
    isNumberOfSearchResultsExhaustive: boolean;
    listingIdResults: number[];
    areCountersOfOrganicAndPartnerListingsExhaustive: boolean;
  };
  onTrackFilterApplied: (filters: object) => void;
  getSearchAlert: () => void;
}

const pickRequiredAnalytics = R.pick(Object.keys(RequiredAnalyticsFilters));
const pickMapParams = R.pick(['latitude', 'longitude', 'zoomLevel']);

export default class SearchRoot extends React.Component<Props> {
  componentDidMount() {
    const { getSearchAlert } = this.props;

    getSearchAlert();
  }

  componentDidUpdate(prevProps: Props) {
    const prevFilters = pickRequiredAnalytics(prevProps.searchParams);
    const filters = pickRequiredAnalytics(this.props.searchParams);

    const prevMapParams = pickMapParams(prevProps.searchParams);
    const mapParams = pickMapParams(this.props.searchParams);

    if (
      !R.equals(prevFilters, filters) ||
      (!R.isEmpty(prevMapParams) && !R.equals(prevMapParams, mapParams))
    ) {
      const changedFilters = Object.keys(filters).filter(
        k => !R.equals(prevFilters[k], filters[k]),
      );
      const changedMapParams = Object.keys(mapParams).filter(
        k => prevMapParams[k] !== mapParams[k],
      );

      this.maybeTrackFilterApplied([...changedFilters, ...changedMapParams]);
    }
  }

  maybeTrackFilterApplied(changes: string[]) {
    const { filtersAnalytics } = this.props;

    const analytics = {
      ...filtersAnalytics,
      searchChanges: changes,
    };

    if (!R.isEmpty(analytics)) {
      this.props.onTrackFilterApplied(analytics);
    }
  }

  render() {
    const {
      error,
      notFound,
      cityCanonical,
      countryCode,
      pageName,
      listingsAnalytics,
      searchParams: { kinds },
      filtersAnalytics: { endDate, startDate },
    } = this.props;

    if (error) {
      return <SomethingWentWrongPage />;
    }
    if (notFound) {
      return <NotFoundPage />;
    }

    return (
      <Page
        name={pageName}
        category={SEARCH}
        traits={{
          ...listingsAnalytics,
          cityCanonical,
          countryCode,
        }}
        remarketingTag={{
          hrental_pagetype: 'searchresults',
          hrental_startdate: startDate,
          hrental_enddate: endDate,
          hrental_city: cityCanonical,
          hrental_country: countryCode,
          hrental_search_category: kinds, // TODO: change to categories and map to old values
        }}
        hasFooter={false}
        hideIntercom
        excludeLegacyCSSBundle={false}
      >
        <SearchLayout />
      </Page>
    );
  }
}
